import Controller from "./application_controller"

/**
 * Controls the hoisting of modals rendered in the DOM, and the maintains the
 * state classes and attributes on the document and application wrapper.
 *
 * Usage:
 *   1. Add controller to the document:
 *      <html data-controller="modals">
 *   2. Link the application wrapper as a target:
 *      <div id="app" data-modals-target="application"></div>
 *   3. Link the modals container as a target:
 *      <div id="modals-container" data-modals-target="container"></div>
 *   4. When rendering a modal, link the modal as a target. This controller
 *      will hoist it into the modals container.
 *   5. When there is a modal, this controller will:
 *       1. Add the `data-modal-open` attribute to the document
 *       2. Add the `inert` attribute to the application wrapper.
 *       3. Fixes the application wrapper to the screen, and offsets
 *          it based on what the scroll position was.
 *   6. When the modal is closed, it will reverse the updates made in Step 5,
 *      and scroll the window to the previous scroll position (if applicable).
 */
export default class extends Controller {
  static identifier = "modals"
  static targets = ["application", "container", "frame", "modal"]

  connect() {
    this.addEventListener('turbo:before-cache', () => {
      this.enableApplication()
    })
  }

  modalClosedEventListener = event => this.didCloseModal(event.target)

  hasOpenModal() {
    return this.modalTargets.some(modal => modal.open);
  }

  async didCloseModal(modal) {
    const animations = modal.getAnimations({ subtree: true })

    try {
      await Promise.all(animations.map(animation => animation.finished))
    } catch (error) {
      console.error(error)
    }

    modal.remove()

    if (!this.hasOpenModal()) {
      this.enableApplication()
    }
  }

  showModal(modal) {
    this.disableApplication()
    modal.showModal()
  }

  frameTargetConnected(frame) {
    frame.addEventListener('turbo:before-frame-render', event => {
      // If there is an open modal in the frame, the replacement modal
      // dialog should be able to skip enter animations, assuming it
      // is a replacement of the existing modal from a form submission.
      if (this.modalTargets.some(modal => modal.open && modal.parentElement === frame)) {
        const dialog = event.detail.newFrame.querySelector('dialog')
        if (dialog) dialog.dataset.didReplaceDialog = true
      }
    })
  }

  modalTargetConnected(modal) {
    // Ensure modals are ignored for turbo caching
    modal.dataset.turboTemporary = true

    requestAnimationFrame(() => {
      if (!this.containerTarget.contains(modal)) {
        this.containerTarget.appendChild(modal)
      }

      modal.addEventListener('close', this.modalClosedEventListener)
      this.showModal(modal)
    })
  }

  modalTargetDisconnected(modal) {
    if (!this.hasOpenModal()) {
      this.enableApplication()
    }
  }

  /**
   * Disables the application wrapper and saves scroll position.
   */
  disableApplication() {
    if (this.element.dataset.modalOpen) return

    this.element.dataset.modalOpen = true
    this.applicationTarget.inert = true

    const { innerHeight, scrollY } = window
    const { scrollHeight, style } = this.applicationTarget


    style.setProperty('position', 'fixed')
    style.setProperty('inset', 0)
    style.setProperty('overflow', 'hidden')

    if (scrollY !== 0 || scrollHeight > innerHeight) {
      this.element.style.setProperty('scrollbar-gutter', 'stable')
    }

    if (scrollY !== 0) {
      style.setProperty('top', -1 * scrollY + 'px')
      window.scrollTo({ top: 0, behavior: 'instant' })
    }

    // Allow the modal container to be visible
    this.containerTarget.dataset.modalOpen = true
  }

  enableApplication() {
    if (!this.element.dataset.modalOpen) return

    // Allow the modal container to hide
    delete this.containerTarget.dataset.modalOpen

    let style = this.applicationTarget.style
    let scrollOffset = style.removeProperty('top')

    style.removeProperty('position')
    style.removeProperty('inset')
    style.removeProperty('overflow')

    this.element.style.removeProperty('scrollbar-gutter')

    if (scrollOffset) {
      window.scrollTo({ top: parseFloat(scrollOffset)*-1, behavior: 'instant' })
    }

    this.applicationTarget.inert = false
    delete this.element.dataset.modalOpen
  }
}
